import React, { useEffect, useState } from 'react';
import { BasicFetch } from '../utils/BasicFetch.ts';
import { Morph } from '../types/Morph';
import { ModuleTitle } from './utilities/ModuleTitle.tsx';
import { Button, Table, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { ReptoolModal } from './utilities/ReptoolModal.tsx';
import { PhotoUploader } from './utilities/PhotoUploader.tsx';
import { UploadHelpModal } from './utilities/MorphDetailModal.tsx';
import { Reptile } from '../types/Reptile.ts';

const DifficultyBadge = ({ difficulty }: { difficulty: number }) => {
  let badgeColor = 'success';
  if (difficulty > 1) {
    badgeColor = 'warning';
  }
  if (difficulty > 2) {
    badgeColor = 'danger';
  }
  return (
    <Badge bg={badgeColor} className='text-light' style={{
      fontSize: '1rem',
    }}>
      {difficulty <= 1 ? 'Easy' : difficulty === 2 ? 'Medium' : 'Hard'}
    </Badge>
  )
}

const UploadModal = ({ closeModal, morph }: {
  morph: Morph,
  closeModal: () => void,
}) => {
  const [currentPhoto, setCurrentPhoto] = useState<Blob | null>(null);
  const [clearPhoto, setClearPhoto] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [uploading, setUploading] = useState(false);

  const submitPhoto = () => {
    if (!currentPhoto) {
      return;
    }

    let formData = new FormData();
    let filename = `${morph}-${new Date().toISOString()}.jpg`

    formData.append("file", currentPhoto, filename);
    formData.append("reptile", morph.reptile);
    formData.append("morph", morph.name);

    BasicFetch({
      url: `${(window as any).serverLocation}/reptile/add_picture`,
      data: formData,
      method: 'POST',
      contentType: 'multipart/form-data',
      onSuccess: (data) => {
        window.alert("Successfully submitted picture for approval.")
        setCurrentPhoto(null);
        closeModal();
      },
      onFail: () => {
        window.alert("There was a problem submitting your photo. Please try again later.")
      },
      onFinish: () => {
        setUploading(false);
      }
    })
  }

  useEffect(() => {
    if (clearPhoto) {
      setCurrentPhoto(null);
      setClearPhoto(false);
    }
  }, [clearPhoto])
  return (
    <>
      {showHelp && <UploadHelpModal closeModal={() => setShowHelp(false)} />}
      <ReptoolModal title={`${morph.reptile_name} - ${morph.name}`} closeModal={() => closeModal()} body={
        <div className='text-light'>
          <p>Upload a photo of the morph, this photo once approved by the moderator will be used to help other users identify this morph.</p>
          <p>Please check out the guideline for what the photo should look like otherwise the submission may be rejected.</p>
          <Button variant='dark' className='mb-3' onClick={() => setShowHelp(true)}>View Guidelines</Button>
          <PhotoUploader setUploadPhoto={setCurrentPhoto} clearPhoto={clearPhoto} />
          {currentPhoto && <div className='d-flex flex-column justify-content-center mt-4 col-4 mx-auto'>
            <Button variant='secondary' className='mb-3' onClick={() => setClearPhoto(true)}>Retake Photo</Button>
            <Button disabled={uploading} onClick={submitPhoto}>
              {uploading ? 'Uploading...' : 'Submit Photo'}
            </Button>
          </div>}
        </div>
      } />
    </>

  )
}


const BountyItem = ({
  morph,
  setSelectedMorph
}: {
  morph: Morph
  setSelectedMorph: (morph: Morph) => void
}) => {

  return (
    <tr>
      <td>{morph.name}</td>
      <td>{morph.reptile_name}</td>
      <td className='text-center'>
        <div className='mx-auto mb-2'><DifficultyBadge difficulty={morph.genes.length} /></div>
        <Button variant='outline-light' onClick={() => setSelectedMorph(morph)}>
          <FontAwesomeIcon icon={faCamera} />
        </Button>
      </td>
    </tr>
  )
}



export const BountyList = ({
  backToMenu,
  reptiles
}: {
  backToMenu: () => void,
  reptiles: Reptile[]
}) => {
  const [fetching, setFetching] = useState(true);
  const [list, setList] = useState<Morph[]>([])
  const [selectedMorph, setSelectedMorph] = useState<Morph | null>(null);
  const [desiredReptile, setDesiredReptile] = useState<string | null>(null);

  const fetchList = () => {
    BasicFetch({
      url: `${(window as any).serverLocation}morph_photo/bounty_list`,
      extra: desiredReptile ? { reptile: desiredReptile } : {},
      onSuccess: ({ bounty_list }) => {
        setList(bounty_list);
      }, onFinish: () => {
        setFetching(false);
      }
    })
  }

  useEffect(() => {
    if (fetching) {
      fetchList()
    }
  }, [fetching])

  useEffect(() => {
    setFetching(true);
  }, [desiredReptile])


  return (
    <div className='mx-3 d-flex flex-column'>
      {selectedMorph && <UploadModal closeModal={() => setSelectedMorph(null)} morph={selectedMorph} />}
      <ModuleTitle title='Bounty List' backToMenu={backToMenu} />
      <p>
        Below is a list of common reptile morphs that are missing images in the app. Upload an approved photo, and earn scales (points) as a reward! It’s a fun way to help build the app’s photo collection while earning rewards.
      </p>

      <select className='form-select mb-3 bg-dark text-light' value={desiredReptile ?? ''} onChange={(e) => {
        setDesiredReptile(e.target.value);
      }
      }>
        <option value=''>All Reptiles</option>
        {reptiles.map(r => (
          <option key={r.accronym} value={r.accronym}>{r.reptile_name}</option>
        ))}
      </select>

      <Table variant='dark' striped>
        <thead>
          <tr>
            <th>Morph Name</th>
            <th>Reptile</th>
            <th className='text-center'>Difficulty</th>
          </tr>
        </thead>
        <tbody>
          {fetching && !list.length && <tr><td colSpan={3}>Loading...</td></tr>}
          {!fetching && list.map(morph => (
            <BountyItem key={morph.id} morph={morph} setSelectedMorph={setSelectedMorph} />
          ))}
        </tbody>
      </Table>
    </div>
  )

}